import { Injectable, inject } from '@angular/core'
import {
  CompanyForm,
  Company,
  CompanyFilterResult,
  CompanyFilterInput,
} from '../types/company'
import type {
  CompanyConnectionRequest,
  ConnectedCompaniesResponse,
} from '../types/companyConnection'
import { environment } from '../../environments/environment'
import { Observable, catchError, map } from 'rxjs'
import { HttpClient, HttpParams } from '@angular/common/http'
import { ErrorService } from './error.service'

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'companies'

  /**
   * Creates the company
   * @param {CompanyForm} company The company data
   * @returns {Observable<boolean>} The request response
   */
  create(company: CompanyForm): Observable<boolean> {
    return this.http.post<boolean>(this.URL, company).pipe(
      map((res: boolean) => {
        return res || false
      }),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Gets all companies
   * @returns {Observable<Array<Company>>} The list of companies
   */
  getAll(): Observable<Array<Company>> {
    return this.http
      .get<Array<Company>>(this.URL)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Toggles the active status of a company
   * @param {string} companyId The ID of the company to toggle
   * @returns {Observable<{message: string, success: boolean}>} The request response
   */
  toggleCompanyActive(
    companyId: string,
  ): Observable<{ message: string; success: boolean }> {
    return this.http
      .put<{
        message: string
        success: boolean
      }>(`${this.URL}/${companyId}/toggle-active`, {})
      .pipe(
        map((response: { message: string; success: boolean }) => {
          return response
        }),
        catchError(this.errorService.handleError),
      )
  }

  /**
   * Fetches a paginated list of companies matching the provided filters.
   * @param {CompanyFilterInput} filter Filter criteria (page is required; name is optional).
   * @returns {Observable<CompanyFilterResult>} Result containing totalRecords and results.
   */
  filterCompanies(filter: CompanyFilterInput): Observable<CompanyFilterResult> {
    let params = new HttpParams().set('page', String(filter.page))
    params = params.set('visibility', filter.visibility ?? 'all')
    params = params.set('pageSize', String(filter.pageSize ?? 10))

    const name = filter.name?.trim()
    if (name) {
      params = params.set('name', name)
    }

    return this.http
      .get<CompanyFilterResult>(`${this.URL}/filter`, { params })
      .pipe(catchError(this.errorService.handleError))
  }

  setMarketplacePublication(
    companyId: string,
    isMarketplacePublic: boolean,
  ): Observable<{ isMarketplacePublic: boolean }> {
    return this.http
      .put<{ isMarketplacePublic: boolean }>(
        `${this.URL}/${companyId}/marketplace-publication`,
        { isMarketplacePublic },
      )
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Autocompletes companies by name
   * @param {string} name Company name query (2+ characters).
   * @param {number} [limit] Optional maximum number of results to return.
   * @returns {Observable<Array<Company>>} List of matching companies.
   */
  autocompleteCompanies(
    name: string,
    limit?: number,
  ): Observable<Array<Company>> {
    let params = new HttpParams().set('name', name.trim())

    if (limit !== undefined && limit !== null) {
      params = params.set('limit', String(limit))
    }

    return this.http
      .get<Array<Company>>(`${this.URL}/autocomplete`, { params })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Creates a mutual (two-way) connection between the given company and the connected company.
   *
   * Backend creates both directions:
   * - companyId -> connectedCompanyId
   * - connectedCompanyId -> companyId
   *
   * @param {CompanyConnectionRequest} payload Connection request containing both company IDs.
   * @returns {Observable<boolean>} Emits true when the connection is created successfully.
   */
  createMutualConnection(
    payload: CompanyConnectionRequest,
  ): Observable<boolean> {
    const url = `${this.URL}/${payload.companyId}/connections`

    return this.http
      .post<boolean>(url, { connectedCompanyId: payload.connectedCompanyId })
      .pipe(
        map((res: boolean) => res || false),
        catchError(this.errorService.handleError),
      )
  }

  /**
   * Deletes a mutual (two-way) connection between the given company and the connected company.
   *
   * Backend removes both directions:
   * - companyId -> connectedCompanyId
   * - connectedCompanyId -> companyId
   *
   * @param {CompanyConnectionRequest} payload Connection request containing both company IDs.
   * @returns {Observable<boolean>} Emits true when the connection is removed successfully.
   */
  deleteMutualConnection(
    payload: CompanyConnectionRequest,
  ): Observable<boolean> {
    const url = `${this.URL}/${payload.companyId}/connections`

    return this.http
      .delete<boolean>(url, {
        body: { connectedCompanyId: payload.connectedCompanyId },
      })
      .pipe(
        map((res: boolean) => res || false),
        catchError(this.errorService.handleError),
      )
  }

  /**
   * Retrieves connected companies from GET /companies/:companyId/connections.
   *
   * @param {string} companyId The company ID to retrieve connected companies for.
   * @returns {Observable<ConnectedCompaniesResponse>} Emits the active connected companies.
   */
  getConnectedCompanies(
    companyId: string,
  ): Observable<ConnectedCompaniesResponse> {
    return this.http
      .get<ConnectedCompaniesResponse>(`${this.URL}/${companyId}/connections`)
      .pipe(
        map((companies) => companies || []),
        catchError(this.errorService.handleError),
      )
  }

  /**
   * Retrieves the current company's connections from GET /companies/connections.
   * The API derives the company ID from the user's session.
   * @returns {Observable<ConnectedCompaniesResponse>} Emits the active connected companies.
   */
  getMyConnectedCompanies(): Observable<ConnectedCompaniesResponse> {
    return this.http
      .get<ConnectedCompaniesResponse>(`${this.URL}/connections`)
      .pipe(
        map((companies) => companies || []),
        catchError(this.errorService.handleError),
      )
  }
}
